import React from 'react';
import { Gift, Star, Check, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { businessInfo } from '../data/mock';

const LoyaltySection = () => {
  const rewards = [
    { visit: 1, reward: null, label: "1st Visit" },
    { visit: 2, reward: "10%", label: "2nd Massage", discount: "10% Discount" },
    { visit: 3, reward: null, label: "3rd" },
    { visit: 4, reward: null, label: "4th" },
    { visit: 5, reward: null, label: "5th" },
    { visit: 6, reward: "15%", label: "6th Massage", discount: "15% Discount" },
    { visit: 7, reward: null, label: "7th" },
    { visit: 8, reward: null, label: "8th" },
    { visit: 9, reward: null, label: "9th" },
    { visit: 10, reward: "20%", label: "10th Massage", discount: "20% Discount" },
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-warm-50 via-white to-sage-50 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-10 left-10 w-32 h-32 bg-sage-200/30 rounded-full blur-2xl" />
      <div className="absolute bottom-10 right-10 w-48 h-48 bg-warm-200/30 rounded-full blur-2xl" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Gift size={18} />
            <span>Rewards Program</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-stone-800 mb-6">
            Loyalty
            <span className="block text-sage-600">Program</span>
          </h2>
          <p className="text-stone-600 text-lg">
            We value our returning clients! Enjoy exclusive discounts as you continue 
            your wellness journey with us.
          </p>
        </div>

        {/* Loyalty Card Visual */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-sage-700 via-sage-600 to-sage-800 rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden">
            {/* Card Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
            </div>

            {/* Card Header */}
            <div className="relative text-center mb-10">
              <div className="inline-flex items-center gap-2 text-sage-200 mb-2">
                <Sparkles size={20} />
                <span className="text-sm font-medium tracking-wider uppercase">Dublin Holistic Massage</span>
                <Sparkles size={20} />
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold text-white">
                Your Loyalty Rewards Card
              </h3>
            </div>

            {/* Stamps Grid */}
            <div className="relative grid grid-cols-5 gap-3 md:gap-4 mb-8">
              {rewards.map((item) => (
                <div
                  key={item.visit}
                  className={`relative aspect-square rounded-xl flex flex-col items-center justify-center transition-all ${
                    item.reward 
                      ? 'bg-amber-400 text-stone-800 shadow-lg' 
                      : 'bg-white/20 text-white border-2 border-white/30 border-dashed'
                  }`}
                >
                  {item.reward ? (
                    <>
                      <span className="text-xl md:text-2xl font-bold">{item.reward}</span>
                      <span className="text-xs font-medium">OFF</span>
                    </>
                  ) : (
                    <span className="text-lg md:text-xl font-semibold">{item.visit}</span>
                  )}
                </div>
              ))}
            </div>

            {/* Rewards Summary */}
            <div className="relative grid md:grid-cols-3 gap-4 text-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <div className="text-2xl font-bold text-amber-300">10%</div>
                <div className="text-sm text-sage-100">2nd Massage Discount</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <div className="text-2xl font-bold text-amber-300">15%</div>
                <div className="text-sm text-sage-100">6th Massage Discount</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <div className="text-2xl font-bold text-amber-300">20%</div>
                <div className="text-sm text-sage-100">10th Massage Discount</div>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="mt-12 max-w-3xl mx-auto">
          <h4 className="text-lg font-semibold text-stone-800 text-center mb-6">How It Works</h4>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-sage-600 font-bold">1</span>
              </div>
              <p className="text-stone-600 text-sm">Book and enjoy your massage sessions with us</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-sage-600 font-bold">2</span>
              </div>
              <p className="text-stone-600 text-sm">We track your visits automatically</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-sage-600 font-bold">3</span>
              </div>
              <p className="text-stone-600 text-sm">Receive discounts on your 2nd, 6th, and 10th massage</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Button
            onClick={() => window.open(businessInfo.squareBookingUrl, '_blank')}
            className="bg-sage-600 hover:bg-sage-700 text-white px-8 py-4 rounded-full text-lg transition-all duration-300 hover:shadow-lg"
          >
            Start Earning Rewards
          </Button>
        </div>
      </div>
    </section>
  );
};

export default LoyaltySection;
